/**
 * FilePickerButton — bouton qui ouvre le picker fichier natif (S2.5e).
 *
 * Pattern courant : un bouton stylé qui déclenche un `<input type="file">`
 * caché. Encapsule l'`<input>` brut (interdit en dehors de
 * `components/ui/` par `sgraphic/no-raw-html-controls`) et expose une API
 * propre.
 *
 *   <FilePickerButton
 *     accept="application/pdf,.pdf"
 *     onPick={(file) => upload(file)}
 *     disabled={isUploading}
 *     variant="primary"
 *   >
 *     <Upload size={14} aria-hidden="true" />
 *     Choisir un PDF
 *   </FilePickerButton>
 *
 * Différence avec `<FileDropZone>` : pas de surface drag-drop, pas de
 * liste de fichiers. Cas d'usage = remplacer un fichier unique avec un
 * simple bouton, sans l'overhead UI du dropzone (Settings, modals, etc.).
 */

import { useRef, type ChangeEvent, type ReactNode } from 'react'

import Button from './Button'

type ButtonVariant = 'primary' | 'neutral' | 'ghost' | 'quiet' | 'accent' | 'danger'
type ButtonSize = 'sm' | 'md' | 'lg'

interface FilePickerButtonProps {
  onPick: (file: File) => void
  /** Filtre `<input accept="...">` natif. */
  accept?: string
  disabled?: boolean
  variant?: ButtonVariant
  size?: ButtonSize
  ariaLabel?: string
  className?: string
  children: ReactNode
}

export default function FilePickerButton({
  onPick,
  accept,
  disabled,
  variant = 'neutral',
  size = 'md',
  ariaLabel,
  className,
  children,
}: FilePickerButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  const handleClick = () => {
    inputRef.current?.click()
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    // Reset l'input pour permettre un re-pick du même fichier (sinon le
    // change event ne se redéclenche pas avec le même path).
    e.target.value = ''
    if (file) onPick(file)
  }

  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={size}
        disabled={disabled}
        onClick={handleClick}
        aria-label={ariaLabel}
        className={className}
      >
        {children}
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="sr-only"
        aria-hidden="true"
        tabIndex={-1}
        onChange={handleChange}
      />
    </>
  )
}
